import { apiDisplayMessage } from "./error-message";
import { consoleRequestOperation, recordConsoleRequestFailure, type ConsoleRequestDiagnostic } from "./request-diagnostics";
import { notifyConsoleConfigurationChanged } from "./read-invalidation";

let csrfToken: string | null = null;
let csrfTokenRequest: Promise<string> | null = null;

/** Event emitter for session expiry — AuthContext listens to this */
type SessionExpiredListener = () => void;
const sessionExpiredListeners: SessionExpiredListener[] = [];

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.push(listener);
  return () => {
    const idx = sessionExpiredListeners.indexOf(listener);
    if (idx >= 0) sessionExpiredListeners.splice(idx, 1);
  };
}

export function notifySessionExpired(): void {
  for (const listener of sessionExpiredListeners) {
    listener();
  }
}

async function fetchCsrfToken(clientRequestId?: string): Promise<string> {
  if (csrfTokenRequest) return await csrfTokenRequest;
  csrfTokenRequest = (async () => {
    const res = await fetch(consoleBffRequestUrl("/console-bff/auth/csrf"), {
      credentials: "include",
      ...(clientRequestId ? { headers: { "x-console-request-id": clientRequestId } } : {}),
    });
    if (!res.ok) throw new Error("操作の準備ができませんでした。ページを再読み込みしてください。");
    const data = (await res.json()) as { csrfToken: string };
    csrfToken = data.csrfToken;
    return csrfToken;
  })();
  try {
    return await csrfTokenRequest;
  } finally {
    csrfTokenRequest = null;
  }
}

export async function getConsoleBffCsrfToken() {
  return await fetchCsrfToken();
}

export function clearConsoleBffCsrfToken() {
  csrfToken = null;
  csrfTokenRequest = null;
}

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    _message: string,
    public requestId?: string,
    public details?: {
      blockedReason?: string;
      retryability?: string;
      validation?: unknown;
      diagnostic?: unknown;
    },
    public retryable?: boolean,
    public retryAfterSeconds?: number,
  ) {
    super(apiDisplayMessage(code, status));
    this.name = "ApiError";
  }
}

export function apiErrorMessage(error: unknown, fallback = "Fetch failed") {
  if (error instanceof ApiError) {
    return error.requestId ? `${error.message}（requestId: ${error.requestId}）` : error.message;
  }
  return error instanceof Error ? error.message : fallback;
}

export async function apiClient<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const started = performance.now();
  const diagnostic: ConsoleRequestDiagnostic = {
    clientRequestId: crypto.randomUUID(),
    operation: consoleRequestOperation(path),
    method: (options.method ?? "GET").toUpperCase(),
    phase: "request",
    startedAt: new Date().toISOString(),
    durationMs: 0,
    status: null,
    errorClass: "",
  };
  try {
    const result = await performApiRequest<T>(path, options, diagnostic);
    if (MUTATION_METHODS.has(diagnostic.method) && diagnostic.status !== 202) {
      notifyConsoleConfigurationChanged(path);
    }
    return result;
  } catch (error) {
    diagnostic.durationMs = Math.round(performance.now() - started);
    diagnostic.errorClass = error instanceof ApiError ? "ApiError" : error instanceof SyntaxError ? "SyntaxError" : error instanceof TypeError ? "TypeError" : "RequestError";
    recordConsoleRequestFailure(diagnostic);
    if (error instanceof TypeError && diagnostic.status === null) {
      throw new ApiError(0, "network_error", "サーバーとの通信に失敗しました。通信状況を確認して、もう一度操作してください。", diagnostic.clientRequestId);
    }
    throw error;
  }
}

async function performApiRequest<T>(
  path: string,
  options: RequestInit,
  diagnostic: ConsoleRequestDiagnostic,
): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
    "x-console-request-id": diagnostic.clientRequestId,
  };
  const hasBody = options.body !== undefined && options.body !== null;
  const contentTypeHeaderKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === "content-type",
  );

  if (hasBody && !contentTypeHeaderKey) {
    headers["content-type"] = "application/json";
  }

  if (MUTATION_METHODS.has(method)) {
    if (!csrfToken) {
      diagnostic.phase = "csrf";
      await fetchCsrfToken(diagnostic.clientRequestId);
    }
    headers["x-csrf-token"] = csrfToken!;
  }

  const url = path.startsWith("/console-bff/")
    ? path
    : `/console-bff/api${path.startsWith("/") ? path : `/${path}`}`;
  const requestUrl = consoleBffRequestUrl(url);

  diagnostic.phase = "request";
  let res = await fetch(requestUrl, {
    ...options,
    method,
    headers,
    credentials: "include",
  });
  diagnostic.phase = "response";
  diagnostic.status = res.status;

  let forbiddenBody: {
    code?: string;
    message?: string;
    requestId?: string;
  } | null = null;

  if (res.status === 403) {
    // CSRF token may have expired — retry once
    forbiddenBody = (await res.json().catch(() => null)) as {
      code?: string;
      message?: string;
      requestId?: string;
    } | null;
    if (forbiddenBody?.code === "CSRF_INVALID" && MUTATION_METHODS.has(method)) {
      csrfToken = null;
      diagnostic.phase = "csrf";
      diagnostic.status = null;
      await fetchCsrfToken(diagnostic.clientRequestId);
      headers["x-csrf-token"] = csrfToken!;
      diagnostic.phase = "request";
      diagnostic.status = null;
      res = await fetch(requestUrl, {
        ...options,
        method,
        headers,
        credentials: "include",
      });
      diagnostic.phase = "response";
      diagnostic.status = res.status;
      forbiddenBody = null;
    }
  }

  if (res.status === 401) {
    const body = await res.json().catch(() => null) as { code?: string; message?: string; requestId?: string } | null;
    // A rejected secondary proof does not invalidate the authenticated session.
    if (body?.code === "authentication_failed") {
      throw new ApiError(401, body.code, body.message ?? res.statusText, body.requestId, apiErrorDetails(body));
    }
    notifySessionExpired();
    throw new ApiError(401, "session_expired", "Session expired");
  }

  if (!res.ok) {
    const body =
      forbiddenBody ??
      ((await res.json().catch(() => ({}))) as {
        code?: string;
        message?: string;
        requestId?: string;
      });
    throw new ApiError(
      res.status,
      body.code ?? "UNKNOWN",
      body.message ?? res.statusText,
      body.requestId,
      apiErrorDetails(body),
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function consoleBffRequestUrl(path: string): string {
  if (!path.startsWith("/console-bff/")) throw new Error("console_bff_path_invalid");
  const location = globalThis.location;
  if (!location) return path;
  return resolveConsoleBffRequestUrl(path, new URL(location.href));
}

export function resolveConsoleBffRequestUrl(path: string, consoleUrl: URL): string {
  if (!path.startsWith("/console-bff/")) throw new Error("console_bff_path_invalid");
  const apiOrigin = resolveConsoleBffApiOrigin(consoleUrl);
  return apiOrigin ? `${apiOrigin}${path}` : path;
}

export function resolveConsoleBffApiOrigin(consoleUrl: URL): string {
  if (consoleUrl.protocol === "http:" &&
      (consoleUrl.hostname === "localhost" || consoleUrl.hostname === "127.0.0.1" ||
        consoleUrl.hostname === "[::1]")) return "";
  if (consoleUrl.protocol !== "https:" || consoleUrl.port || consoleUrl.username || consoleUrl.password) {
    throw new Error("console_bff_environment_binding_unavailable");
  }
  if (consoleUrl.hostname === "drive.test-extone.app") return "https://api.drive.test-extone.app";
  if (consoleUrl.hostname === "drive.extone.app") return "https://api.drive.extone.app";
  const development = /^console-drive-extone-([a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?)\.c-rise\.uk$/u
    .exec(consoleUrl.hostname);
  if (development) return `https://api-drive-extone-${development[1]}.c-rise.uk`;
  throw new Error("console_bff_environment_binding_unavailable");
}

function apiErrorDetails(body: {
  code?: unknown;
  message?: unknown;
  requestId?: unknown;
  blockedReason?: unknown;
  retryability?: unknown;
  validation?: unknown;
  diagnostic?: unknown;
}) {
  return {
    ...(typeof body.blockedReason === "string" ? { blockedReason: body.blockedReason } : {}),
    ...(typeof body.retryability === "string" ? { retryability: body.retryability } : {}),
    ...(body.validation !== undefined ? { validation: body.validation } : {}),
    ...(body.diagnostic !== undefined ? { diagnostic: body.diagnostic } : {}),
  };
}
